import { authStore } from "@/lib/authStore";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, useTheme } from '@/App';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import RichContent from '@/components/RichContent';
import CsjResponsePanel from '@/components/CsjResponsePanel';
import { toast } from 'sonner';
import {
  Upload, FileText, Database, Search, Trash2, ChevronLeft, ChevronRight,
  Loader2, Sun, Moon, Shield, MessageSquare, X, Plus, Filter, Tag,
  BookOpen, Activity, Send, Sparkles, ArrowLeft, Server, Radio, Cloud,
  Cpu, Network, FileSpreadsheet, File, ThumbsUp, ThumbsDown, ShieldCheck,
  ShieldAlert, ShieldX, ChevronDown, AlertCircle, CheckCircle2
} from 'lucide-react';
import {
  uploadFileInChunks,
  fileFingerprint,
  loadResumeState,
  formatBytesPerSecond,
  formatEta,
} from '@/lib/azureChunkedUpload';
import { withTenant, getActiveTenant, setActiveTenant, clearActiveTenant } from '@/lib/tenantImpersonation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  var token = authStore.getAccessToken();
  return { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' };
}

function getAuthHeadersNoContentType() {
  var token = authStore.getAccessToken();
  return { 'Authorization': 'Bearer ' + token };
}

// readBody — read response body safely, exactly once.
// Eliminates the entire class of "body stream already read" errors that can
// surface when error-handling and success paths both try to parse JSON.
async function readBody(response) {
  try {
    var text = await response.text();
    if (!text) return {};
    try { return JSON.parse(text); }
    catch (e) { return { detail: text.slice(0, 500) }; }
  } catch (e) {
    // .text() threw — almost always a mid-response connection drop under load.
    // Return a sentinel that callers can detect and use to retry.
    return {
      _readBodyFailed: true,
      detail: 'Network connection dropped while reading the server response. This usually clears up on retry.'
    };
  }
}

// fetchWithReadRetry — wraps fetch + readBody with up to 4 attempts.
// Cloudflare cuts response streams on burst /initiate calls; this absorbs it.
async function fetchWithReadRetry(url, options) {
  var backoffs = [0, 1500, 4000, 8000];
  var lastError = null;
  for (var attempt = 0; attempt < backoffs.length; attempt++) {
    if (backoffs[attempt] > 0) {
      await new Promise(function(r) { setTimeout(r, backoffs[attempt]); });
    }
    var resp;
    try {
      resp = await fetch(url, options);
    } catch (err) {
      lastError = err;
      continue;
    }
    var body = await readBody(resp);
    if (!body._readBodyFailed) {
      return { ok: resp.ok, status: resp.status, body: body };
    }
  }
  return {
    ok: false,
    status: 0,
    body: { detail: lastError
      ? 'Could not reach server after 4 attempts: ' + (lastError.message || 'network error')
      : 'Upload failed: server unreachable. Please retry with fewer files at once.'
    }
  };
}

const VENDORS = ['Ericsson', 'Nokia', 'Huawei', 'Samsung', 'ZTE', 'Mavenir', 'Other'];
const DOMAINS = ['RAN', 'Core', 'Transport', 'AI/ML', 'Cloud', 'Security', 'Operations', 'Other'];
const DOC_TYPES = ['Manual', 'SOP', 'Training', 'Logs', 'Architecture', 'Specification', 'Report', 'Other'];

// ==================== UPLOAD PANEL ====================
// ==================== CREATABLE VENDOR COMBOBOX ====================
const VendorCombobox = function({ value, onChange, disabled }) {
  var [list, setList] = useState([]);
  var [query, setQuery] = useState(value || '');
  var [open, setOpen] = useState(false);
  var [loading, setLoading] = useState(false);
  var rootRef = useRef(null);

  useEffect(function() { setQuery(value || ''); }, [value]);

  useEffect(function() {
    var load = async function() {
      try {
        var res = await fetch(withTenant(API_URL + '/rag/vendors'), { headers: getAuthHeaders() });
        if (res.ok) {
          var data = await readBody(res);
          setList(data.vendors || []);
        }
      } catch (e) {}
    };
    load();
  }, []);

  // Close dropdown on outside click
  useEffect(function() {
    var handler = function(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function() { document.removeEventListener('mousedown', handler); };
  }, []);

  var q = query.trim();
  var qLower = q.toLowerCase();
  var filtered = [];
  for (var i = 0; i < list.length; i++) {
    if (!q || list[i].name.toLowerCase().indexOf(qLower) !== -1) filtered.push(list[i]);
  }
  var exactMatch = false;
  for (var j = 0; j < list.length; j++) {
    if (list[j].name.toLowerCase() === qLower) { exactMatch = true; break; }
  }
  var showCreate = q.length >= 1 && !exactMatch;

  var pick = function(name) {
    setQuery(name);
    onChange(name);
    setOpen(false);
  };

  var createVendor = async function() {
    if (!q) return;
    setLoading(true);
    try {
      var res = await fetch(API_URL + '/rag/vendors', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: q })
      });
      if (res.ok) {
        var data = await readBody(res);
        var v = data.vendor;
        // Refresh list
        setList(function(prev) {
          var dup = false;
          for (var k = 0; k < prev.length; k++) if (prev[k].id === v.id) { dup = true; break; }
          return dup ? prev : prev.concat([v]);
        });
        pick(v.name);
      } else {
        var err = await readBody(res);
        toast.error(err.detail || 'Could not add vendor');
      }
    } catch (e) {
      toast.error('Could not add vendor');
    } finally {
      setLoading(false);
    }
  };

  var items = [];
  for (var k = 0; k < filtered.length; k++) {
    var v = filtered[k];
    items.push(
      <button
        key={v.id}
        type="button"
        onClick={function(n){ return function() { pick(n); }; }(v.name)}
        className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary/70 flex items-center justify-between"
        data-testid={'vendor-option-' + k}
      >
        <span>{v.name}</span>
        {v.seeded === false && <span className="text-[10px] text-primary">custom</span>}
      </button>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <input
        type="text"
        value={query}
        disabled={disabled}
        placeholder="Select or type to add a vendor"
        className="w-full px-3 py-2 rounded-lg bg-secondary/30 border border-border/50 text-sm disabled:opacity-50"
        onFocus={function() { setOpen(true); }}
        onChange={function(e) {
          setQuery(e.target.value);
          onChange(e.target.value);  // fire so parent sees custom values too
          setOpen(true);
        }}
        onKeyDown={function(e) {
          if (e.key === 'Enter' && showCreate) { e.preventDefault(); createVendor(); }
          if (e.key === 'Escape') setOpen(false);
        }}
        data-testid="vendor-combobox-input"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-border/60 bg-popover shadow-lg max-h-60 overflow-y-auto" data-testid="vendor-combobox-dropdown">
          {items.length > 0 ? items : <p className="px-3 py-2 text-xs text-muted-foreground">No matches</p>}
          {showCreate && (
            <button
              type="button"
              onClick={createVendor}
              disabled={loading}
              className="w-full text-left px-3 py-2 text-sm text-primary border-t border-border/50 bg-primary/5 hover:bg-primary/10 disabled:opacity-50"
              data-testid="vendor-create-option"
            >
              {loading ? 'Adding...' : 'Create "' + q + '"'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MULTI-FILE UPLOAD PANEL ====================
// Supports: multi-file selection, folder upload (webkitdirectory),
// drag-and-drop (files + folders), per-file queue with up to MAX_CONCURRENT
// parallel uploads, per-file progress + retry, batch aggregate summary.

const MAX_CONCURRENT = 2;  // Outer queue: at most 2 files actively uploading.
                           // Each file internally uses 2 parallel block workers,
                           // so peak Azure connections = 4 (well under browser/CDN
                           // saturation thresholds). Higher values caused mid-response
                           // connection drops on large folder batches.
const ALLOWED_EXT = ['pdf', 'docx', 'pptx', 'csv', 'xlsx', 'xls'];

// Recursively read all files under a dropped directory entry (DataTransferItem API)
var readEntryAsync = function(entry, pathPrefix) {
  return new Promise(function(resolve) {
    if (entry.isFile) {
      entry.file(function(f) {
        f._relPath = pathPrefix + f.name;
        resolve([f]);
      }, function() { resolve([]); });
    } else if (entry.isDirectory) {
      var reader = entry.createReader();
      var allFiles = [];
      var readBatch = function() {
        reader.readEntries(async function(entries) {
          if (!entries.length) { resolve(allFiles); return; }
          var batchResults = await Promise.all(entries.map(function(e) { return readEntryAsync(e, pathPrefix + entry.name + '/'); }));
          for (var i = 0; i < batchResults.length; i++) {
            for (var j = 0; j < batchResults[i].length; j++) allFiles.push(batchResults[i][j]);
          }
          readBatch();  // keep reading until empty batch
        }, function() { resolve(allFiles); });
      };
      readBatch();
    } else {
      resolve([]);
    }
  });
};

var extractFilesFromDataTransfer = async function(dataTransfer) {
  var items = dataTransfer.items;
  var files = [];
  if (items && items.length && items[0].webkitGetAsEntry) {
    var entryPromises = [];
    for (var i = 0; i < items.length; i++) {
      var entry = items[i].webkitGetAsEntry && items[i].webkitGetAsEntry();
      if (entry) entryPromises.push(readEntryAsync(entry, ''));
    }
    var results = await Promise.all(entryPromises);
    for (var r = 0; r < results.length; r++) {
      for (var k = 0; k < results[r].length; k++) files.push(results[r][k]);
    }
  } else {
    // Fallback: flat file list
    for (var f = 0; f < dataTransfer.files.length; f++) files.push(dataTransfer.files[f]);
  }
  return files;
};

var isAllowedFile = function(name) {
  var ext = (name.split('.').pop() || '').toLowerCase();
  return ALLOWED_EXT.indexOf(ext) !== -1;
};

// ----- /initiate throttle -----
// Cloudflare's bot-mitigation cuts response streams when the same client
// fires many requests at one endpoint in a short window. Stagger /initiate
// calls so no two go out within MIN_INITIATE_GAP_MS of each other.
var lastInitiateAt = 0;
var MIN_INITIATE_GAP_MS = 600;
async function reserveInitiateSlot() {
  var now = Date.now();
  var wait = (lastInitiateAt + MIN_INITIATE_GAP_MS) - now;
  if (wait > 0) await new Promise(function(r) { setTimeout(r, wait); });
  lastInitiateAt = Date.now();
}

// ==================== BULK INGEST FROM EXISTING BLOB STORAGE ====================
// Operators upload files directly to Azure Blob (azcopy / Storage Explorer / CI),
// then point this panel at the prefix. Backend discovers, dedupes, queues
// background indexing — no browser-side file picking, no chunked re-upload.

const BulkIngestPanel = function({ onIngestStarted }) {
  var [prefix, setPrefix] = useState('');
  var [batchLabel, setBatchLabel] = useState('');
  var [vendorDefault, setVendorDefault] = useState('');
  var [domainDefault, setDomainDefault] = useState('');
  var [docTypeDefault, setDocTypeDefault] = useState('');
  var [preview, setPreview] = useState(null);
  var [previewing, setPreviewing] = useState(false);
  var [submitting, setSubmitting] = useState(false);
  var [lastResult, setLastResult] = useState(null);

  async function handlePreview() {
    if (!prefix.trim()) {
      toast.error('Enter a blob prefix first');
      return;
    }
    setPreviewing(true);
    setPreview(null);
    try {
      // Dry-run gives us full metadata + dedupe info, more useful than /preview
      var res = await fetch(withTenant(API_URL + '/rag/ingest/from-blob'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prefix: prefix.trim(),
          batch_label: batchLabel.trim() || null,
          vendor_default: vendorDefault.trim() || '',
          domain_default: domainDefault.trim() || '',
          doc_type_default: docTypeDefault.trim() || '',
          dry_run: true,
        }),
      });
      var data = await readBody(res);
      if (!res.ok) {
        toast.error((data && data.detail) || ('Preview failed (HTTP ' + res.status + ')'));
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error('Network error during preview');
    } finally {
      setPreviewing(false);
    }
  }

  async function handleStart() {
    if (!preview) {
      toast.error('Run preview first');
      return;
    }
    var newCount = (preview.eligible || 0) - (preview.plan || []).filter(function(p) { return p.duplicate; }).length;
    if (newCount === 0) {
      toast.message('Nothing new to ingest — all eligible files are already indexed.');
      return;
    }
    setSubmitting(true);
    try {
      var res = await fetch(withTenant(API_URL + '/rag/ingest/from-blob'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prefix: prefix.trim(),
          batch_label: batchLabel.trim() || null,
          vendor_default: vendorDefault.trim() || '',
          domain_default: domainDefault.trim() || '',
          doc_type_default: docTypeDefault.trim() || '',
          dry_run: false,
        }),
      });
      var data = await readBody(res);
      if (!res.ok) {
        toast.error((data && data.detail) || ('Ingest failed (HTTP ' + res.status + ')'));
        return;
      }
      setLastResult(data);
      toast.success('Queued ' + (data.queued || 0) + ' file(s) for indexing.' + (data.duplicates ? ' Skipped ' + data.duplicates + ' duplicate(s).' : ''));
      if (onIngestStarted) onIngestStarted(data);
      // Clear preview so the user must re-discover before starting another batch
      setPreview(null);
    } catch (e) {
      toast.error('Network error starting ingest');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border/50 bg-card p-5 space-y-4" data-testid="bulk-ingest-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-base flex items-center gap-2">
            <Cloud className="h-4 w-4 text-primary" />
            Bulk ingest from Azure Blob
          </h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Already pushed files to blob via <code className="rounded bg-muted px-1">azcopy</code> or
            Storage Explorer? Point this at the prefix — we&rsquo;ll discover the files, parse
            <strong> vendor/domain/doc_type</strong> from the folder structure, dedupe against
            existing docs, and run indexing in the background. No browser upload required.
          </p>
        </div>
      </div>

      <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-1">
        <p className="font-medium">Folder convention</p>
        <code className="block">&lt;prefix&gt;/&lt;vendor&gt;/&lt;domain&gt;/&lt;doc_type&gt;/&lt;file&gt;</code>
        <p className="text-muted-foreground">
          Example: <code>kb-intake/feb2026/Samsung/RAN/SOP/xyz.pdf</code> → vendor=Samsung, domain=RAN, doc_type=SOP
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-xs font-medium">
          Blob prefix *
          <input
            type="text"
            value={prefix}
            onChange={function(e) { setPrefix(e.target.value); }}
            placeholder="kb-intake/feb2026"
            data-testid="bulk-ingest-prefix"
            className="mt-1 w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs font-medium">
          Batch label (optional)
          <input
            type="text"
            value={batchLabel}
            onChange={function(e) { setBatchLabel(e.target.value); }}
            placeholder="Samsung Feb 2026 dump"
            data-testid="bulk-ingest-batch-label"
            className="mt-1 w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs font-medium">
          Default vendor (used if folder structure is shallow)
          <input
            type="text" value={vendorDefault}
            onChange={function(e) { setVendorDefault(e.target.value); }}
            data-testid="bulk-ingest-vendor-default"
            className="mt-1 w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs font-medium">
          Default domain
          <input
            type="text" value={domainDefault}
            onChange={function(e) { setDomainDefault(e.target.value); }}
            data-testid="bulk-ingest-domain-default"
            className="mt-1 w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="text-xs font-medium md:col-span-2">
          Default doc_type
          <input
            type="text" value={docTypeDefault}
            onChange={function(e) { setDocTypeDefault(e.target.value); }}
            data-testid="bulk-ingest-doctype-default"
            className="mt-1 w-full rounded-md border border-border/50 bg-background px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          className="rounded-full"
          onClick={handlePreview}
          disabled={previewing || !prefix.trim()}
          data-testid="bulk-ingest-preview-btn"
        >
          {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Search className="h-4 w-4 mr-1.5" />}
          Discover &amp; preview
        </Button>
        <Button
          size="sm"
          className="rounded-full"
          onClick={handleStart}
          disabled={submitting || !preview}
          data-testid="bulk-ingest-start-btn"
        >
          {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
          Start indexing
        </Button>
      </div>

      {preview && (
        <div className="rounded-lg border border-border/50 bg-background/40 p-3 space-y-2 text-xs" data-testid="bulk-ingest-preview-result">
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <span><strong>Discovered:</strong> {preview.discovered}</span>
            <span><strong>Eligible:</strong> {preview.eligible}</span>
            <span className="text-amber-500"><strong>Already indexed:</strong> {(preview.plan || []).filter(function(p) { return p.duplicate; }).length}</span>
            <span><strong>Skipped:</strong> {(preview.skipped || []).length}</span>
          </div>
          {(preview.plan || []).length > 0 && (
            <div className="max-h-48 overflow-auto border border-border/30 rounded mt-2">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0">
                  <tr><th className="text-left p-1.5">File</th><th className="text-left p-1.5">Vendor</th><th className="text-left p-1.5">Domain</th><th className="text-left p-1.5">Doc Type</th><th className="text-left p-1.5">Status</th></tr>
                </thead>
                <tbody>
                  {(preview.plan || []).slice(0, 50).map(function(p) {
                    return (
                      <tr key={p.blob_path} className="border-t border-border/20">
                        <td className="p-1.5 font-mono truncate max-w-[16rem]" title={p.blob_path}>{p.filename}</td>
                        <td className="p-1.5">{p.vendor || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-1.5">{p.domain || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-1.5">{p.doc_type || <span className="text-muted-foreground">—</span>}</td>
                        <td className="p-1.5">
                          {p.duplicate
                            ? <span className="text-amber-500">already indexed</span>
                            : <span className="text-emerald-500">new</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {(preview.plan || []).length > 50 && (
                <p className="text-xs text-muted-foreground p-2">… and {preview.plan.length - 50} more.</p>
              )}
            </div>
          )}
        </div>
      )}

      {lastResult && lastResult.batch_id && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs" data-testid="bulk-ingest-last-result">
          <p className="font-medium">Batch <code>{lastResult.batch_id}</code> queued — {lastResult.queued} file(s) indexing in background.</p>
          <p className="text-muted-foreground mt-1">Refresh the document list below to track per-file progress; failed files can be retried without re-upload.</p>
        </div>
      )}
    </div>
  );
};



const UploadPanel = function({ onUploadComplete, prefill }) {
  var [vendor, setVendor] = useState((prefill && prefill.vendor) || '');
  var [domain, setDomain] = useState((prefill && prefill.domain) || '');
  var [docType, setDocType] = useState((prefill && prefill.docType) || '');
  var [batchLabel, setBatchLabel] = useState('');
  var [keepMetadata, setKeepMetadata] = useState(false);  // "Apply same metadata for next upload"
  var [queue, setQueue] = useState([]);  // [{ id, file, relPath, status, progress, error, jobId }]
  var [running, setRunning] = useState(false);
  var [batchId, setBatchId] = useState(null);
  var [dragOver, setDragOver] = useState(false);

  // Refs for stable access from async workers (queue state can be stale in closures)
  var queueRef = useRef([]);
  var activeCountRef = useRef(0);
  var cancelledRef = useRef(false);

  useEffect(function() {
    queueRef.current = queue;
  }, [queue]);

  useEffect(function() {
    if (prefill) {
      if (prefill.vendor) setVendor(prefill.vendor);
      if (prefill.domain) setDomain(prefill.domain);
      if (prefill.docType) setDocType(prefill.docType);
    }
  }, [prefill]);

  var updateItem = function(id, patch) {
    setQueue(function(prev) {
      var out = [];
      for (var i = 0; i < prev.length; i++) {
        out.push(prev[i].id === id ? Object.assign({}, prev[i], patch) : prev[i]);
      }
      return out;
    });
  };

  var addFiles = function(fileList) {
    var skipped = 0;
    var accepted = [];
    for (var i = 0; i < fileList.length; i++) {
      var f = fileList[i];
      if (!isAllowedFile(f.name)) { skipped++; continue; }
      accepted.push({
        id: 'q-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
        file: f,
        relPath: f._relPath || (f.webkitRelativePath || ''),
        status: 'pending',
        progress: 0,
        error: '',
        jobId: null,
        chunks: 0,
      });
    }
    if (skipped > 0) toast.warning(skipped + ' unsupported file(s) skipped');
    if (accepted.length === 0) return;
    setQueue(function(prev) { return prev.concat(accepted); });
  };

  var handleFileInput = function(e) {
    if (e.target.files && e.target.files.length) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  };

  var handleDrop = async function(e) {
    e.preventDefault();
    setDragOver(false);
    var files = await extractFilesFromDataTransfer(e.dataTransfer);
    if (files.length) addFiles(files);
  };

  var removeItem = function(id) {
    setQueue(function(prev) {
      var out = [];
      for (var i = 0; i < prev.length; i++) if (prev[i].id !== id) out.push(prev[i]);
      return out;
    });
  };

  var clearCompleted = function() {
    setQueue(function(prev) {
      var out = [];
      for (var i = 0; i < prev.length; i++) if (prev[i].status !== 'completed') out.push(prev[i]);
      return out;
    });
  };

  // ---- Single-file pipeline (initiate -> PUT -> complete -> poll) ----
  var uploadOne = function(item, bId) {
    return new Promise(function(resolve) {
      (async function() {
        try {
          updateItem(item.id, { status: 'uploading', progress: 0, error: '' });
          // Wait for /initiate slot — staggers requests across the queue so
          // Cloudflare's bot-mitigation doesn't cut response streams.
          await reserveInitiateSlot();
          // Initiate (with auto-retry on transient body-read failures)
          var initR = await fetchWithReadRetry(withTenant(API_URL + '/rag/upload/initiate'), {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              file_name: item.file.name,
              file_type: item.file.type || 'application/octet-stream',
              file_size: item.file.size,
              vendor: vendor,
              domain: domain,
              doc_type: docType,
              batch_id: bId,
              batch_label: batchLabel || null,
              folder_path: item.relPath || null
            })
          });
          if (!initR.ok) {
            throw new Error((initR.body && initR.body.detail) || ('Initiate failed (HTTP ' + initR.status + ')'));
          }
          var init = initR.body;
          if (!init || !init.upload_url || !init.job_id) {
            throw new Error('Initiate response missing upload_url/job_id');
          }
          updateItem(item.id, { jobId: init.job_id });

          // Detect resume — if we have prior block state for this exact file,
          // surface a one-line note so the admin knows we're not starting over.
          try {
            var fp = fileFingerprint(item.file);
            var prior = loadResumeState(fp);
            if (prior && prior.uploadedIdxs && prior.uploadedIdxs.length > 0) {
              var pct = Math.round((prior.uploadedIdxs.length / prior.totalBlocks) * 100);
              toast.info('Resuming ' + item.file.name + ' from ' + pct + '% (browser remembered ' + prior.uploadedIdxs.length + '/' + prior.totalBlocks + ' blocks)');
            }
          } catch (e) { /* ignore */ }

          // Chunked upload (extracted module). Handles GB-scale, resumable,
          // parallel + per-block retry, fresh-SAS rotation if it expires.
          await uploadFileInChunks(item.file, init.upload_url, {
            onProgress: function(p) {
              // Map upload's 0..100 onto file row's 0..50 (final 50..100 = indexing)
              updateItem(item.id, {
                progress: Math.round(p.pct * 0.5),
                mbps: p.mbPerSec,
                etaSec: p.etaSec,
              });
            },
            resolveSasUrl: async function() {
              // Original SAS expired mid-upload — mint a fresh one server-side.
              var resp = await fetch(withTenant(API_URL + '/rag/upload/' + init.job_id + '/resume-sas'), {
                method: 'POST',
                headers: getAuthHeaders()
              });
              if (!resp.ok) {
                var eb = await readBody(resp);
                throw new Error((eb && eb.detail) || ('SAS refresh failed (HTTP ' + resp.status + ')'));
              }
              var data = await readBody(resp);
              return data.upload_url;
            },
          });

          updateItem(item.id, { status: 'processing', progress: 50, mbps: null, etaSec: null });

          // Complete (with auto-retry on transient body-read failures)
          var cR = await fetchWithReadRetry(withTenant(API_URL + '/rag/upload/complete'), {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ job_id: init.job_id, blob_path: init.blob_path })
          });
          if (!cR.ok) {
            throw new Error((cR.body && cR.body.detail) || ('Complete failed (HTTP ' + cR.status + ')'));
          }

          // Poll status — generous timeout for large pptx with OCR fallback
          // (which can legitimately take 30-45 min on the backend).
          var attempts = 0;
          var maxAttempts = 1200;  // 1200 × 3s = 60 min upper bound
          while (attempts++ < maxAttempts) {
            await new Promise(function(r) { setTimeout(r, 3000); });
            if (cancelledRef.current) throw new Error('Cancelled');
            try {
              var sRes = await fetch(withTenant(API_URL + '/rag/documents/' + init.job_id + '/status'), { headers: getAuthHeaders() });
              if (!sRes.ok) continue;
              var s = await readBody(sRes);
              var mapped = 50 + Math.round((s.progress || 0) * 0.5);
              updateItem(item.id, { progress: mapped });
              if (s.status === 'completed') {
                updateItem(item.id, { status: 'completed', progress: 100, chunks: s.total_chunks || 0 });
                resolve();
                return;
              }
              if (s.status === 'failed') {
                updateItem(item.id, { status: 'failed', error: s.error || 'Indexing failed' });
                resolve();
                return;
              }
            } catch (e) { /* transient */ }
          }
          updateItem(item.id, { status: 'failed', error: 'Indexing timed out' });
          resolve();
        } catch (err) {
          updateItem(item.id, { status: 'failed', error: err.message || 'Upload failed' });
          resolve();
        }
      })();
    });
  };

  // ---- Concurrency-limited batch runner ----
  var startUpload = async function(retryOnly) {
    if (!vendor || !domain || !docType) { toast.error('Select vendor, domain and doc type'); return; }
    var pending = queueRef.current.filter(function(q) {
      return retryOnly ? q.status === 'failed' : (q.status === 'pending' || q.status === 'failed');
    });
    if (pending.length === 0) { toast.info('Nothing to upload'); return; }

    setRunning(true);
    cancelledRef.current = false;
    var bId = batchId || ('batch-' + Date.now());
    setBatchId(bId);

    // Reset failed -> pending so they retry
    var resetPatch = {};
    for (var p = 0; p < pending.length; p++) resetPatch[pending[p].id] = true;
    setQueue(function(prev) {
      var out = [];
      for (var i = 0; i < prev.length; i++) {
        if (resetPatch[prev[i].id]) out.push(Object.assign({}, prev[i], { status: 'pending', progress: 0, error: '' }));
        else out.push(prev[i]);
      }
      return out;
    });

    // Worker pool
    var idx = 0;
    var items = pending.slice();  // snapshot
    var worker = async function() {
      while (idx < items.length) {
        if (cancelledRef.current) return;
        var myIdx = idx++;
        activeCountRef.current++;
        await uploadOne(items[myIdx], bId);
        activeCountRef.current--;
      }
    };
    var workers = [];
    for (var w = 0; w < Math.min(MAX_CONCURRENT, items.length); w++) workers.push(worker());
    await Promise.all(workers);

    // ---- Auto-retry pass: for first-attempt batches, retry failed files ONCE
    // automatically. This recovers from transient network drops without admin
    // intervention. Skip if this run was already a retry-only pass.
    if (!retryOnly && !cancelledRef.current) {
      var initialFailures = queueRef.current.filter(function(q) { return q.status === 'failed'; });
      if (initialFailures.length > 0 && initialFailures.length < items.length) {
        // Some succeeded, some failed → retry the failures with 5s grace period
        toast.info(initialFailures.length + ' file(s) failed — auto-retrying in 5s…');
        await new Promise(function(r) { setTimeout(r, 5000); });

        // Reset failed -> pending and run a second pass
        var retryItems = initialFailures.slice();
        var resetMap = {};
        for (var ri = 0; ri < retryItems.length; ri++) resetMap[retryItems[ri].id] = true;
        setQueue(function(prev) {
          var out = [];
          for (var k = 0; k < prev.length; k++) {
            if (resetMap[prev[k].id]) out.push(Object.assign({}, prev[k], { status: 'pending', progress: 0, error: '' }));
            else out.push(prev[k]);
          }
          return out;
        });

        var ridx = 0;
        var rWorker = async function() {
          while (ridx < retryItems.length) {
            if (cancelledRef.current) return;
            var myIdx = ridx++;
            activeCountRef.current++;
            await uploadOne(retryItems[myIdx], bId);
            activeCountRef.current--;
          }
        };
        var rWorkers = [];
        for (var rw = 0; rw < Math.min(MAX_CONCURRENT, retryItems.length); rw++) rWorkers.push(rWorker());
        await Promise.all(rWorkers);
      }
    }

    setRunning(false);
    if (onUploadComplete) onUploadComplete();
    var completedNow = queueRef.current.filter(function(q) { return q.status === 'completed'; }).length;
    var failedNow = queueRef.current.filter(function(q) { return q.status === 'failed'; }).length;
    if (failedNow === 0) toast.success('All ' + completedNow + ' file(s) indexed');
    else toast.warning(completedNow + ' indexed, ' + failedNow + ' failed');

    // Session reset: if the whole batch closed out cleanly (no failures to retry)
    // and the admin did NOT opt into "Apply same metadata for next upload",
    // explicitly clear everything so the next batch starts fresh.
    if (failedNow === 0 && completedNow > 0 && !keepMetadata) {
      setVendor('');
      setDomain('');
      setDocType('');
      setBatchLabel('');
      setBatchId(null);
      setQueue([]);
      toast.info('Upload completed. Metadata has been reset for a new batch.');
    } else if (failedNow === 0 && completedNow > 0 && keepMetadata) {
      // Metadata retained per admin choice — only clear the completed queue + batch id
      // so the next drop starts a brand-new batch id with the SAME metadata.
      setBatchId(null);
      setQueue([]);
      toast.info('Metadata retained. Drop more files to start the next batch.');
    }
  };

  // ---- Aggregate batch progress ----
  var total = queue.length;
  var completed = 0; var failed = 0; var processing = 0; var uploading = 0;
  for (var qi = 0; qi < queue.length; qi++) {
    var s = queue[qi].status;
    if (s === 'completed') completed++;
    else if (s === 'failed') failed++;
    else if (s === 'processing') processing++;
    else if (s === 'uploading') uploading++;
  }
  var overallPct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
  var hasFailed = failed > 0;

  // Pre-build option elements
  var domainOptions = [<option key="" value="">Select domain</option>];
  for (var di = 0; di < DOMAINS.length; di++) { domainOptions.push(<option key={DOMAINS[di]} value={DOMAINS[di]}>{DOMAINS[di]}</option>); }
  var docTypeOptions = [<option key="" value="">Select type</option>];
  for (var dti = 0; dti < DOC_TYPES.length; dti++) { docTypeOptions.push(<option key={DOC_TYPES[dti]} value={DOC_TYPES[dti]}>{DOC_TYPES[dti]}</option>); }

  // Pre-build queue rows (no .map in JSX)
  var queueRows = [];
  for (var qj = 0; qj < queue.length; qj++) {
    var q = queue[qj];
    var statusColor = 'text-muted-foreground';
    var StatusIcon = Loader2;
    var iconCls = '';
    if (q.status === 'completed') { statusColor = 'text-green-600 dark:text-green-400'; StatusIcon = CheckCircle2; }
    else if (q.status === 'failed') { statusColor = 'text-red-600 dark:text-red-400'; StatusIcon = AlertCircle; }
    else if (q.status === 'uploading' || q.status === 'processing') { statusColor = 'text-primary'; StatusIcon = Loader2; iconCls = 'animate-spin'; }
    else { StatusIcon = FileText; }
    queueRows.push(
      <div key={q.id} className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0" data-testid={'queue-row-' + qj}>
        <StatusIcon className={'h-4 w-4 flex-shrink-0 ' + statusColor + ' ' + iconCls} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium truncate" title={q.relPath || q.file.name}>
              {q.relPath || q.file.name}
            </p>
            <span className={'text-[10px] flex-shrink-0 ' + statusColor} data-testid={'queue-status-' + qj}>
              {q.status === 'uploading' && (
                'Uploading ' + q.progress + '%' +
                (q.mbps ? ' · ' + formatBytesPerSecond(q.mbps) : '') +
                (q.etaSec != null ? ' · ETA ' + formatEta(q.etaSec) : '')
              )}
              {q.status === 'processing' && 'Indexing ' + q.progress + '%'}
              {q.status === 'completed' && 'Indexed · ' + q.chunks + ' chunks'}
              {q.status === 'failed' && 'Failed'}
              {q.status === 'pending' && 'Queued'}
            </span>
          </div>
          <div className="h-1 mt-1 bg-muted rounded-full overflow-hidden">
            <div className={'h-full transition-all ' + (q.status === 'failed' ? 'bg-red-500' : q.status === 'completed' ? 'bg-green-500' : 'bg-primary')} style={{ width: q.progress + '%' }} />
          </div>
          {q.error && <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 truncate" title={q.error} data-testid={'queue-error-' + qj}>{q.error}</p>}
        </div>
        {!running && q.status !== 'uploading' && q.status !== 'processing' && (
          <button onClick={function(id){ return function() { removeItem(id); }; }(q.id)} className="p-1 rounded hover:bg-secondary" data-testid={'queue-remove-' + qj}>
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/50 bg-card p-5 space-y-4" data-testid="upload-panel">
      <h3 className="font-semibold flex items-center gap-2">
        <Upload className="h-4 w-4 text-primary" /> Upload Documents
      </h3>

      {prefill && prefill.query && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs" data-testid="upload-suggestion-banner">
          <p className="font-medium text-amber-700 dark:text-amber-400 mb-0.5">Closing a knowledge gap</p>
          <p className="text-muted-foreground">Users asked: <span className="italic">"{prefill.query}"</span></p>
          {prefill.docType && <p className="text-muted-foreground mt-1">Suggested doc type: <span className="font-medium text-foreground">{prefill.docType}</span></p>}
        </div>
      )}

      {/* File/folder pickers + drag-drop zone */}
      <div
        className={'block border-2 border-dashed rounded-xl p-6 text-center transition-colors ' + (dragOver ? 'border-primary bg-primary/10' : 'border-border/50 hover:border-primary/30 hover:bg-primary/5')}
        onDragOver={function(e) { e.preventDefault(); setDragOver(true); }}
        onDragLeave={function() { setDragOver(false); }}
        onDrop={handleDrop}
        data-testid="file-dropzone"
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Drag files or an entire folder here</p>
        <p className="text-xs text-muted-foreground/70 mt-1">PDF, DOCX, PPTX, CSV, XLSX — GB-scale, batch-ready</p>
        <div className="flex flex-wrap gap-2 justify-center mt-3">
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary/80 text-xs" data-testid="pick-files-label">
            <FileText className="h-3.5 w-3.5" /> Pick files
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,.csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileInput}
              data-testid="file-input"
            />
          </label>
          <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary hover:bg-secondary/80 text-xs" data-testid="pick-folder-label">
            <Database className="h-3.5 w-3.5" /> Pick folder
            <input
              type="file"
              webkitdirectory=""
              directory=""
              multiple
              className="hidden"
              onChange={handleFileInput}
              data-testid="folder-input"
            />
          </label>
        </div>
      </div>

      {/* Metadata tags (applied to entire batch) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground">Batch metadata</p>
          <p className="text-[10px] text-muted-foreground/70" data-testid="batch-metadata-note">
            All files in this batch will use the same metadata
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Vendor</label>
            <VendorCombobox value={vendor} onChange={setVendor} disabled={running} />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Domain</label>
            <select value={domain} onChange={function(e) { setDomain(e.target.value); }} disabled={running} className="w-full px-3 py-2 rounded-lg bg-secondary/30 border border-border/50 text-sm disabled:opacity-50" data-testid="domain-select">
              {domainOptions}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Document Type</label>
            <select value={docType} onChange={function(e) { setDocType(e.target.value); }} disabled={running} className="w-full px-3 py-2 rounded-lg bg-secondary/30 border border-border/50 text-sm disabled:opacity-50" data-testid="doctype-select">
              {docTypeOptions}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_auto] gap-3 mt-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">
              Batch label <span className="text-muted-foreground/60">(optional — used for filtering & analytics)</span>
            </label>
            <input
              type="text"
              value={batchLabel}
              disabled={running}
              onChange={function(e) { setBatchLabel(e.target.value); }}
              placeholder='e.g. "Q1-2026 Ericsson RAN manuals"'
              maxLength={120}
              className="w-full px-3 py-2 rounded-lg bg-secondary/30 border border-border/50 text-sm disabled:opacity-50"
              data-testid="batch-label-input"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 cursor-pointer select-none" data-testid="keep-metadata-toggle-label">
            <input
              type="checkbox"
              checked={keepMetadata}
              disabled={running}
              onChange={function(e) { setKeepMetadata(e.target.checked); }}
              className="h-3.5 w-3.5 rounded border-border/60 text-primary focus:ring-primary"
              data-testid="keep-metadata-toggle"
            />
            <span className="text-xs text-muted-foreground">Apply same metadata for next upload</span>
          </label>
        </div>
      </div>

      {/* Batch aggregate + queue */}
      {total > 0 && (
        <div className="space-y-2" data-testid="queue-block">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium" data-testid="batch-summary">
              {running ? 'Processing ' + (uploading + processing) + ' · ' : ''}
              {completed} of {total} completed
              {failed > 0 && <span className="text-red-600 dark:text-red-400"> · {failed} failed</span>}
            </span>
            <div className="flex items-center gap-2">
              {completed > 0 && !running && (
                <button onClick={clearCompleted} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="clear-completed-btn">Clear completed</button>
              )}
              <span className="text-muted-foreground tabular-nums">{overallPct}%</span>
            </div>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: overallPct + '%' }} data-testid="overall-progress" />
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-border/30 bg-background/40 px-2" data-testid="queue-list">
            {queueRows}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={function() { startUpload(false); }} disabled={total === 0 || running} className="flex-1 rounded-full" data-testid="upload-btn">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {running ? 'Uploading...' : (total === 0 ? 'Upload & Index' : 'Upload ' + total + ' file(s)')}
        </Button>
        {hasFailed && !running && (
          <Button onClick={function() { startUpload(true); }} variant="outline" className="rounded-full" data-testid="retry-failed-btn">
            Retry {failed} failed
          </Button>
        )}
      </div>
    </div>
  );
};

// ==================== DOCUMENT LIST ====================
// Server-side pagination, filtering, search. All counts authoritative from backend.
const PAGE_SIZE = 50;

const DocumentList = function({
  documents, total, totalAll, statusCounts, page, pageSize,
  filters, onFilterChange, onPageChange,
  onDelete, onRetry, onBulkDelete, onBulkRetry, loading,
}) {
  var [selected, setSelected] = useState({});
  var [bulkDeleting, setBulkDeleting] = useState(false);
  var [bulkRetrying, setBulkRetrying] = useState(false);

  // Reset selection when page or filters change
  useEffect(function() { setSelected({}); }, [page, filters]);

  var pageCount = Math.max(1, Math.ceil(total / pageSize));
  var startDoc = total === 0 ? 0 : page * pageSize + 1;
  var endDoc = Math.min((page + 1) * pageSize, total);

  var allPageIds = (documents || []).map(function(d) { return d.id; });
  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; });
  var allChecked = allPageIds.length > 0 && allPageIds.every(function(id) { return selected[id]; });
  var someChecked = selectedIds.length > 0;

  // Detect whether all selected docs can be retried (queued or error)
  var selectedDocs = (documents || []).filter(function(d) { return selected[d.id]; });
  var retryableDocs = selectedDocs.filter(function(d) { return d.status === 'error' || d.status === 'failed' || d.status === 'queued'; });
  var canBulkRetry = retryableDocs.length > 0 && retryableDocs.length === selectedIds.length;

  function toggleAll() {
    if (allChecked) { setSelected({}); return; }
    var next = {};
    allPageIds.forEach(function(id) { next[id] = true; });
    setSelected(next);
  }

  function toggleOne(id) {
    setSelected(function(prev) {
      var next = Object.assign({}, prev);
      next[id] = !next[id];
      return next;
    });
  }

  async function handleBulkDelete() {
    if (!window.confirm('Delete ' + selectedIds.length + ' selected document(s)?')) return;
    setBulkDeleting(true);
    try {
      if (onBulkDelete) await onBulkDelete(selectedIds);
      setSelected({});
    } finally {
      setBulkDeleting(false);
    }
  }

  async function handleBulkRetry() {
    setBulkRetrying(true);
    try {
      if (onBulkRetry) await onBulkRetry(retryableDocs);
      setSelected({});
    } finally {
      setBulkRetrying(false);
    }
  }

  var statusTabs = [
    { key: 'all',     label: 'All',     count: totalAll },
    { key: 'indexed', label: 'Indexed', count: statusCounts && statusCounts.indexed },
    { key: 'queued',  label: 'Queued',  count: statusCounts && statusCounts.queued },
    { key: 'error',   label: 'Error',   count: statusCounts && statusCounts.error },
  ];

  var rows = (documents || []).map(function(doc) {
    var isChecked = !!selected[doc.id];
    var docId = doc.id;
    var statusCls = doc.status === 'indexed'
      ? 'bg-green-500/10 text-green-600'
      : (doc.status === 'error' || doc.status === 'failed')
        ? 'bg-red-500/10 text-red-600'
        : 'bg-amber-500/10 text-amber-600';
    var uploadedAt = doc.uploaded_at || doc.upload_date;
    var indexedAt = doc.indexed_at;
    return (
      <tr key={docId} className={'border-b border-border/30 transition-colors ' + (isChecked ? 'bg-primary/5' : 'hover:bg-muted/30')} data-testid={'doc-row-' + docId}>
        <td className="px-3 py-2.5">
          <input type="checkbox" checked={isChecked} onChange={function() { toggleOne(docId); }} className="rounded border-border accent-primary h-4 w-4 cursor-pointer" />
        </td>
        <td className="px-3 py-2.5 max-w-[260px]">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary flex-shrink-0" />
            <p className="text-xs font-medium truncate" title={doc.filename}>{doc.filename}</p>
          </div>
        </td>
        <td className="px-3 py-2.5">
          {doc.vendor && <span className="inline-flex px-1.5 py-0.5 rounded-full text-[11px] bg-blue-500/10 text-blue-600 dark:text-blue-400">{doc.vendor}</span>}
        </td>
        <td className="px-3 py-2.5">
          {doc.domain && <span className="inline-flex px-1.5 py-0.5 rounded-full text-[11px] bg-green-500/10 text-green-600 dark:text-green-400">{doc.domain}</span>}
        </td>
        <td className="px-3 py-2.5">
          {doc.doc_type && <span className="inline-flex px-1.5 py-0.5 rounded-full text-[11px] bg-orange-500/10 text-orange-600">{doc.doc_type}</span>}
        </td>
        <td className="px-3 py-2.5 text-xs tabular-nums text-right">{(doc.chunk_count || 0).toLocaleString()}</td>
        <td className="px-3 py-2.5">
          <span className={'inline-flex px-1.5 py-0.5 rounded-full text-[11px] font-medium ' + statusCls}>{doc.status || 'unknown'}</span>
        </td>
        <td className="px-3 py-2.5 text-[11px] text-muted-foreground whitespace-nowrap">
          {doc.uploaded_by ? <span className="block truncate max-w-[100px]" title={doc.uploaded_by}>{doc.uploaded_by.split('@')[0]}</span> : '—'}
        </td>
        <td className="px-3 py-2.5 text-[11px] text-muted-foreground whitespace-nowrap">
          {uploadedAt ? new Date(uploadedAt).toLocaleDateString() : '—'}
        </td>
        <td className="px-3 py-2.5 text-[11px] text-muted-foreground whitespace-nowrap">
          {indexedAt ? new Date(indexedAt).toLocaleDateString() : '—'}
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-1">
            {(doc.status === 'error' || doc.status === 'failed' || doc.status === 'queued') && onRetry && (
              <button onClick={function() { onRetry(doc); }} className="p-1.5 rounded-lg hover:bg-amber-500/10 text-amber-600 transition-colors" title="Retry indexing" data-testid={'retry-doc-' + docId}>
                <Loader2 className="h-3.5 w-3.5" />
              </button>
            )}
            <button onClick={function() { onDelete(docId, doc.filename); }} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-600 transition-colors" data-testid={'delete-doc-' + docId}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </td>
      </tr>
    );
  });

  return (
    <div className="rounded-xl border border-border/50 overflow-hidden bg-card" data-testid="document-list">

      {/* Filter toolbar */}
      <div className="px-4 py-2.5 bg-muted/30 border-b border-border/40 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="text"
              value={filters.search || ''}
              onChange={function(e) { onFilterChange({ search: e.target.value }); }}
              placeholder="Search by filename…"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg bg-background border border-border/50 focus:border-primary/40 focus:outline-none"
            />
          </div>
          {/* Vendor */}
          <select value={filters.vendor || ''} onChange={function(e) { onFilterChange({ vendor: e.target.value }); }} className="px-2.5 py-1.5 text-xs rounded-lg bg-background border border-border/50">
            <option value="">All Vendors</option>
            {VENDORS.map(function(v) { return <option key={v} value={v}>{v}</option>; })}
          </select>
          {/* Domain */}
          <select value={filters.domain || ''} onChange={function(e) { onFilterChange({ domain: e.target.value }); }} className="px-2.5 py-1.5 text-xs rounded-lg bg-background border border-border/50">
            <option value="">All Domains</option>
            {DOMAINS.map(function(d) { return <option key={d} value={d}>{d}</option>; })}
          </select>
          {/* Sort */}
          <select value={filters.sort_by || 'newest'} onChange={function(e) { onFilterChange({ sort_by: e.target.value }); }} className="px-2.5 py-1.5 text-xs rounded-lg bg-background border border-border/50">
            <option value="newest">Newest first</option>
            <option value="chunks">Most chunks</option>
            <option value="alpha">A → Z</option>
          </select>
          {/* Clear filters */}
          {(filters.search || filters.vendor || filters.domain || filters.sort_by !== 'newest') && (
            <button onClick={function() { onFilterChange({ search: '', vendor: '', domain: '', sort_by: 'newest' }); }} className="text-xs text-primary hover:underline">Clear</button>
          )}
        </div>

        {/* Status tabs + bulk actions */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {statusTabs.map(function(tab) {
              return (
                <button
                  key={tab.key}
                  onClick={function() { onFilterChange({ status: tab.key }); }}
                  className={'px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ' + ((filters.status || 'all') === tab.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                >
                  {tab.label}
                  {tab.count != null && <span className="ml-0.5 opacity-70">{tab.count}</span>}
                </button>
              );
            })}
          </div>
          {someChecked && (
            <div className="flex items-center gap-2">
              {canBulkRetry && (
                <button onClick={handleBulkRetry} disabled={bulkRetrying} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 border border-amber-500/20 transition-colors disabled:opacity-50">
                  {bulkRetrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Loader2 className="h-3.5 w-3.5" />}
                  Re-index {selectedIds.length}
                </button>
              )}
              <button onClick={handleBulkDelete} disabled={bulkDeleting} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 border border-red-500/20 transition-colors disabled:opacity-50">
                {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Delete {selectedIds.length}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
      ) : documents && documents.length === 0 ? (
        <div className="py-12 text-center" data-testid="empty-docs">
          <Database className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{filters.search || filters.vendor || filters.domain || (filters.status && filters.status !== 'all') ? 'No documents match these filters' : 'No documents ingested yet'}</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b border-border/50">
                <th className="px-3 py-2.5">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} className="rounded border-border accent-primary h-4 w-4 cursor-pointer" title={allChecked ? 'Deselect all' : 'Select page'} />
                </th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Document</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Vendor</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Domain</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                <th className="px-3 py-2.5 text-right text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Chunks</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Uploaded By</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Upload Date</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Last Indexed</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      )}

      {/* Pagination footer */}
      <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/40 bg-muted/20">
        <p className="text-xs text-muted-foreground">
          {total === 0 ? 'No results' : 'Showing ' + startDoc + '–' + endDoc + ' of ' + total.toLocaleString() + ' document' + (total !== 1 ? 's' : '')}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={function() { onPageChange(0); }}
            disabled={page === 0}
            className="px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
            title="First page"
          >‹‹</button>
          <button
            onClick={function() { onPageChange(page - 1); }}
            disabled={page === 0}
            className="px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
          ><ChevronLeft className="h-3.5 w-3.5" /></button>
          {(function() {
            var btns = [];
            var start = Math.max(0, Math.min(page - 2, pageCount - 5));
            var end = Math.min(pageCount, start + 5);
            for (var pi = start; pi < end; pi++) {
              var pNum = pi;
              btns.push(
                <button
                  key={pi}
                  onClick={(function(p) { return function() { onPageChange(p); }; })(pNum)}
                  className={'px-2.5 py-1 rounded text-xs font-medium transition-colors ' + (pNum === page ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
                >{pi + 1}</button>
              );
            }
            return btns;
          })()}
          <button
            onClick={function() { onPageChange(page + 1); }}
            disabled={page >= pageCount - 1}
            className="px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
          ><ChevronRight className="h-3.5 w-3.5" /></button>
          <button
            onClick={function() { onPageChange(pageCount - 1); }}
            disabled={page >= pageCount - 1}
            className="px-2 py-1 rounded text-xs text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:pointer-events-none"
            title="Last page"
          >››</button>
        </div>
      </div>
    </div>
  );
};

// ==================== CONFIDENCE BADGE ====================
// Employees see label only. Admins see label + % score.
const ConfidenceBadge = function({ confidence, showScore }) {
  if (!confidence) return null;
  var level = confidence.level || 'none';
  var styles = {
    high: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', border: 'border-green-500/30', Icon: ShieldCheck, label: 'High Confidence' },
    medium: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-500/30', Icon: ShieldAlert, label: 'Medium Confidence' },
    low: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', border: 'border-red-500/30', Icon: ShieldX, label: 'Low Confidence' },
    none: { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border/50', Icon: ShieldX, label: 'No Match' }
  };
  var s = styles[level] || styles.none;
  var Icon = s.Icon;
  return (
    <span
      className={'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ' + s.bg + ' ' + s.text + ' ' + s.border}
      title={confidence.reason || ''}
      data-testid={'confidence-badge-' + level}
    >
      <Icon className="h-3 w-3" />
      {s.label}
      {showScore && typeof confidence.score === 'number' && (
        <span className="text-[10px] opacity-70">({Math.round((confidence.score || 0) * 100)}%)</span>
      )}
    </span>
  );
};

// ==================== FEEDBACK CONTROL ====================
const FeedbackControl = function({ queryId }) {
  var [rating, setRating] = useState(null); // 'up' | 'down' | null
  var [submitting, setSubmitting] = useState(false);
  var [showReasons, setShowReasons] = useState(false);

  var submit = async function(r, reason) {
    if (!queryId || submitting) return;
    setSubmitting(true);
    try {
      var res = await fetch(withTenant(API_URL + '/rag/feedback'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ query_id: queryId, rating: r, reason: reason || null })
      });
      if (!res.ok) throw new Error('Feedback failed');
      setRating(r);
      setShowReasons(false);
      toast.success('Thanks — feedback recorded');
    } catch (e) {
      toast.error('Could not record feedback');
    } finally {
      setSubmitting(false);
    }
  };

  var handleThumbsDown = function() {
    if (rating === 'down') return;
    setShowReasons(true);
  };

  var reasonButtons = [];
  var reasons = [
    { id: 'incorrect', label: 'Incorrect answer' },
    { id: 'incomplete', label: 'Incomplete answer' },
    { id: 'irrelevant', label: 'Not relevant' }
  ];
  for (var ri = 0; ri < reasons.length; ri++) {
    var r = reasons[ri];
    reasonButtons.push(
      <button
        key={r.id}
        onClick={((rid) => function() { submit('down', rid); })(r.id)}
        disabled={submitting}
        className="px-2.5 py-1 rounded-lg text-[11px] bg-secondary hover:bg-secondary/80 border border-border/50"
        data-testid={'fb-reason-' + r.id}
      >
        {r.label}
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-1.5" data-testid="rag-feedback">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] text-muted-foreground mr-1">Was this helpful?</span>
        <button
          onClick={function() { if (rating !== 'up') submit('up'); }}
          disabled={submitting || !queryId}
          className={'p-1.5 rounded-lg transition-colors ' + (rating === 'up' ? 'bg-green-500/15 text-green-600' : 'text-muted-foreground hover:bg-secondary')}
          data-testid="fb-thumbs-up"
          title="Mark as useful"
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={handleThumbsDown}
          disabled={submitting || !queryId}
          className={'p-1.5 rounded-lg transition-colors ' + (rating === 'down' ? 'bg-red-500/15 text-red-600' : 'text-muted-foreground hover:bg-secondary')}
          data-testid="fb-thumbs-down"
          title="Mark as not useful"
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </button>
      </div>
      {showReasons && rating !== 'down' && (
        <div className="flex flex-wrap gap-1.5 pl-1" data-testid="fb-reason-picker">
          {reasonButtons}
          <button
            onClick={function() { setShowReasons(false); }}
            className="px-2 py-1 rounded-lg text-[11px] text-muted-foreground hover:bg-secondary"
            data-testid="fb-reason-cancel"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN RETRIEVAL DETAILS ====================
const RetrievalDetails = function({ breakdown }) {
  var [open, setOpen] = useState(false);
  if (!breakdown) return null;
  var chunkRows = [];
  var chunks = breakdown.chunks || [];
  for (var ci = 0; ci < chunks.length; ci++) {
    var c = chunks[ci];
    chunkRows.push(
      <div key={ci} className="rounded-lg border border-border/40 bg-muted/20 p-2.5 text-xs">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-medium truncate">{c.filename || 'Unknown'}</span>
          <span className="tabular-nums text-primary">{Math.round((c.score || 0) * 100)}%</span>
        </div>
        <div className="flex flex-wrap gap-1 text-[10px] text-muted-foreground mb-1">
          {c.vendor && <span className="px-1.5 py-0.5 rounded bg-secondary/60">{c.vendor}</span>}
          {c.domain && <span className="px-1.5 py-0.5 rounded bg-secondary/60">{c.domain}</span>}
          {c.page_or_section !== undefined && c.page_or_section !== null && <span>Page/Sec: {c.page_or_section}</span>}
        </div>
        <p className="text-muted-foreground leading-snug">{c.preview}</p>
      </div>
    );
  }
  return (
    <div className="mt-2" data-testid="admin-retrieval-details">
      <button
        onClick={function() { setOpen(!open); }}
        className="flex items-center gap-1.5 text-[11px] text-primary hover:underline"
        data-testid="toggle-retrieval-details"
      >
        <ChevronDown className={'h-3 w-3 transition-transform ' + (open ? 'rotate-180' : '')} />
        {open ? 'Hide' : 'Show'} retrieval details (admin)
      </button>
      {open && (
        <div className="mt-2 space-y-2 p-3 rounded-xl border border-dashed border-border/50 bg-background/50">
          <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
            <span>Top relevance: <span className="text-foreground font-medium">{Math.round((breakdown.top_relevance || 0) * 100)}%</span></span>
            <span>Sources: <span className="text-foreground font-medium">{breakdown.sources_count}</span></span>
            <span>Chunks: <span className="text-foreground font-medium">{breakdown.chunks_retrieved}</span></span>
            <span>Score variance: <span className="text-foreground font-medium tabular-nums">{(breakdown.top_k_score_variance || 0).toFixed(4)}</span></span>
          </div>
          <div className="space-y-1.5">{chunkRows}</div>
        </div>
      )}
    </div>
  );
};

// ==================== QUERY PROGRESS STEPPER ====================
var QUERY_STEPS = [
  { key: 'searching', label: 'Searching', icon: '⟳' },
  { key: 'ranking',   label: 'Ranking',   icon: '⟳' },
  { key: 'generating', label: 'Structuring', icon: '⟳' },
];
var STEP_ORDER = { searching: 0, ranking: 1, generating: 2 };

var QueryProgressStepper = function({ phase, detail }) {
  var currentIdx = STEP_ORDER[phase] !== undefined ? STEP_ORDER[phase] : -1;
  return (
    <div className="space-y-2.5 py-1 min-w-[220px]">
      {QUERY_STEPS.map(function(step, i) {
        var done = i < currentIdx;
        var active = i === currentIdx;
        return (
          <div key={step.key} className={'flex items-center gap-3 transition-opacity ' + (active || done ? 'opacity-100' : 'opacity-30')}>
            <div className={'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ' +
              (done ? 'bg-emerald-500/20 text-emerald-500' : active ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground')}>
              {done ? '✓' : active ? (
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
              ) : String(i + 1)}
            </div>
            <div className="flex-1 min-w-0">
              <span className={'text-xs font-medium ' + (active ? 'text-foreground' : done ? 'text-muted-foreground' : 'text-muted-foreground')}>
                {step.label}
              </span>
              {active && detail && (
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">{detail}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== RAG CHAT ====================
const RAGChat = function({ user }) {
  var isAdmin = user && user.is_admin;
  var [messages, setMessages] = useState([]);
  var [input, setInput] = useState('');
  var [sending, setSending] = useState(false);
  var [vendorFilter, setVendorFilter] = useState('');
  var [domainFilter, setDomainFilter] = useState('');
  var [showFilters, setShowFilters] = useState(false);
  var [streamingMsg, setStreamingMsg] = useState('');
  var [streamingPhase, setStreamingPhase] = useState('');
  var [lastSources, setLastSources] = useState([]);
  var endRef = useRef(null);

  useEffect(function() {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMsg]);

  var sendQuery = async function(overrideQuery) {
    var queryText = overrideQuery || input;
    if (!queryText.trim() || sending) return;
    if (!overrideQuery) setInput('');

    var userMsg = { id: 'u-' + Date.now(), role: 'user', content: queryText.trim() };
    setMessages(function(prev) { return prev.concat([userMsg]); });
    setSending(true);
    setStreamingMsg('Searching knowledge base...');
    setStreamingPhase('searching');
    setLastSources([]);

    try {
      // SSE streaming query via /export/v2/stream-csj.
      // Returns structured CSJ (all 15 block types) with progress events.
      var streamResp = await fetch(withTenant(API_URL + '/export/v2/stream-csj'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          query: queryText.trim(),
          audience: 'engineering',
          top_k: 8,
          vendor_hint: vendorFilter || null,
          domain_hint: domainFilter || null,
        }),
      });

      if (!streamResp.ok) {
        var errBody = await readBody(streamResp);
        throw new Error((errBody && errBody.detail) || 'Query failed');
      }

      var reader = streamResp.body.getReader();
      var decoder = new TextDecoder();
      var buf = '';
      var done = false;

      while (!done) {
        var chunk = await reader.read();
        done = chunk.done;
        if (chunk.value) buf += decoder.decode(chunk.value, { stream: true });

        // Process complete SSE lines from buffer
        var lines = buf.split('\n');
        buf = lines.pop(); // keep incomplete last line
        for (var li = 0; li < lines.length; li++) {
          var line = lines[li].trim();
          if (!line.startsWith('data:')) continue;
          var rawJson = line.slice(5).trim();
          if (!rawJson || rawJson === '[KEEPALIVE]') continue;
          var evt;
          try { evt = JSON.parse(rawJson); } catch (_) { continue; }

          if (evt.phase === 'searching') {
            setStreamingPhase('searching');
            setStreamingMsg('Scanning knowledge base...');
          } else if (evt.phase === 'ranking') {
            setStreamingPhase('ranking');
            setStreamingMsg(evt.docs_found + ' docs · ' + (evt.confidence_level || 'medium') + ' confidence');
          } else if (evt.phase === 'generating') {
            setStreamingPhase('generating');
            setStreamingMsg('Building structured response...');
          } else if (evt.phase === 'keepalive') {
            // no-op — connection alive
          } else if (evt.phase === 'error') {
            throw new Error(evt.message || 'Query failed');
          } else if (evt.phase === 'done') {
            var csj = evt.csj || null;
            var retrieval = evt.retrieval || null;
            var jobId = evt.job_id || null;
            var csjSources = csj && csj.sources && csj.sources.length > 0
              ? csj.sources
              : [];
            var assistantMsg = {
              id: 'a-' + Date.now(),
              role: 'assistant',
              csj: csj,
              job_id: jobId,
              retrieval: retrieval,
              sources: csjSources,
              confidence: retrieval ? {
                level: retrieval.confidence_level,
                score: retrieval.confidence_score,
              } : null,
            };
            setMessages(function(prev) { return prev.concat([assistantMsg]); });
            setStreamingMsg('');
            setStreamingPhase('');
            setLastSources(csjSources);
          }
        }
      }

    } catch (e) {
      toast.error(e.message || 'Query failed');
      var errMsg = { id: 'e-' + Date.now(), role: 'assistant', content: 'Error: ' + (e.message || 'Query failed') };
      setMessages(function(prev) { return prev.concat([errMsg]); });
      setStreamingMsg('');
      setStreamingPhase('');
    } finally {
      setSending(false);
    }
  };

  // Build message elements
  var msgElements = [];
  for (var mi = 0; mi < messages.length; mi++) {
    var msg = messages[mi];
    var isUser = msg.role === 'user';
    msgElements.push(
      <div key={msg.id} className={'flex ' + (isUser ? 'justify-end' : 'justify-start')}>
        <div className={'max-w-[85%] ' + (isUser ? '' : 'flex gap-3')}>
          {!isUser && (
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
              <BookOpen className="h-4 w-4 text-primary" />
            </div>
          )}
          <div className={isUser ? '' : 'flex-1'}>
            <div className={'px-5 py-3 rounded-2xl ' + (isUser ? 'bg-primary text-primary-foreground rounded-tr-sm' : 'bg-card border border-border/50 rounded-tl-sm')}>
              {isUser ? (
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              ) : msg.csj ? (
                <CsjResponsePanel
                  csj={msg.csj}
                  jobId={msg.job_id}
                  retrieval={msg.retrieval}
                  isAdmin={isAdmin}
                />
              ) : (
                <RichContent content={msg.content} />
              )}
            </div>
            {/* Feedback for non-CSJ legacy messages */}
            {!isUser && !msg.csj && (
              <div className="mt-2 space-y-2" data-testid="rag-answer-meta">
                {msg.confidence && (
                  <div className="flex flex-wrap items-center gap-2">
                    <ConfidenceBadge confidence={msg.confidence} showScore={isAdmin} />
                    {isAdmin && msg.confidence.reason && (
                      <span className="text-[11px] text-muted-foreground">{msg.confidence.reason}</span>
                    )}
                  </div>
                )}
                {msg.sources && msg.sources.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="source-citations">
                    {(function() {
                      var srcElems = [];
                      for (var si = 0; si < msg.sources.length; si++) {
                        var src = msg.sources[si];
                        srcElems.push(
                          <span key={si} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-primary/5 text-xs text-muted-foreground border border-border/30">
                            <FileText className="h-3 w-3 text-primary" />
                            {src.filename}
                            {src.page ? ' (p.' + src.page + ')' : ''}
                            {isAdmin && typeof src.score === 'number' && (
                              <span className="text-[10px] text-primary">{Math.round((src.score || 0) * 100)}%</span>
                            )}
                          </span>
                        );
                      }
                      return srcElems;
                    })()}
                  </div>
                )}
                {msg.query_id && <FeedbackControl queryId={msg.query_id} />}
                {isAdmin && msg.retrieval_breakdown && (
                  <RetrievalDetails breakdown={msg.retrieval_breakdown} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="rag-chat">
      {/* Chat Header */}
      <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Ask Your Knowledge Base</h3>
          <span className="text-xs text-muted-foreground">(Enterprise Mode)</span>
          <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-primary/10 text-primary border border-primary/20">v1.0</span>
        </div>
        <button
          onClick={function() { setShowFilters(!showFilters); }}
          className={'p-2 rounded-lg transition-colors ' + (showFilters ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-secondary')}
          data-testid="toggle-rag-filters"
        >
          <Filter className="h-4 w-4" />
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-4 py-2 border-b border-border/30 flex items-center gap-3 bg-muted/20" data-testid="rag-filters">
          <select value={vendorFilter} onChange={function(e) { setVendorFilter(e.target.value); }} className="px-3 py-1.5 rounded-lg bg-background border border-border/50 text-xs" data-testid="rag-vendor-filter">
            {(function() { var opts = [<option key="" value="">All Vendors</option>]; for (var vi = 0; vi < VENDORS.length; vi++) { opts.push(<option key={VENDORS[vi]} value={VENDORS[vi]}>{VENDORS[vi]}</option>); } return opts; })()}
          </select>
          <select value={domainFilter} onChange={function(e) { setDomainFilter(e.target.value); }} className="px-3 py-1.5 rounded-lg bg-background border border-border/50 text-xs" data-testid="rag-domain-filter">
            {(function() { var opts = [<option key="" value="">All Domains</option>]; for (var di = 0; di < DOMAINS.length; di++) { opts.push(<option key={DOMAINS[di]} value={DOMAINS[di]}>{DOMAINS[di]}</option>); } return opts; })()}
          </select>
          {(vendorFilter || domainFilter) && (
            <button onClick={function() { setVendorFilter(''); setDomainFilter(''); }} className="text-xs text-primary hover:underline">Clear</button>
          )}
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
          {messages.length === 0 && !streamingMsg && (
            <div className="py-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto mb-4 text-primary/20" />
              <h3 className="text-lg font-semibold mb-2">Ask Your Knowledge Base</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                Query your organization's ingested documents. Responses are strictly grounded — no hallucination, only verified knowledge with citations.
              </p>
              <div className="flex flex-wrap gap-2 justify-center">
                {(function() {
                  var suggestions = ['What are the prerequisites for 5G RAN deployment?', 'Compare network troubleshooting procedures', 'What security protocols are documented?'];
                  var btns = [];
                  for (var qi = 0; qi < suggestions.length; qi++) {
                    btns.push(
                      <button key={qi} onClick={((q) => function() { sendQuery(q); })(suggestions[qi])} className="px-4 py-2 rounded-full bg-secondary/50 hover:bg-secondary text-xs transition-colors border border-border/50" data-testid={'rag-suggestion-' + qi}>
                        {suggestions[qi]}
                      </button>
                    );
                  }
                  return btns;
                })()}
              </div>
            </div>
          )}

          {msgElements}

          {/* Streaming progress */}
          {streamingPhase && (
            <div className="flex justify-start">
              <div className="max-w-[85%] flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <BookOpen className="h-4 w-4 text-primary animate-pulse" />
                </div>
                <div className="px-5 py-4 rounded-2xl bg-card border border-border/50 rounded-tl-sm">
                  <QueryProgressStepper phase={streamingPhase} detail={streamingMsg} />
                </div>
              </div>
            </div>
          )}

          {sending && !streamingMsg && (
            <div className="flex justify-start">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-primary animate-pulse" />
                </div>
                <div className="px-5 py-4 rounded-2xl bg-card border border-border/50 rounded-tl-sm">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t border-border/50 p-4">
        <div className="max-w-3xl mx-auto relative">
          <textarea
            value={input}
            onChange={function(e) { setInput(e.target.value); }}
            onKeyDown={function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendQuery(); } }}
            placeholder="Ask your knowledge base..."
            disabled={sending}
            className="w-full min-h-[56px] max-h-[200px] pr-14 rounded-2xl resize-none bg-secondary/30 border border-border/50 focus:border-primary/50 focus:ring-1 focus:ring-primary/20 px-5 py-3.5 text-sm"
            rows={1}
            data-testid="rag-chat-input"
          />
          <Button
            onClick={function() { sendQuery(); }}
            disabled={!input.trim() || sending}
            size="icon"
            className="absolute right-2 bottom-2 rounded-full h-10 w-10"
            data-testid="rag-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN KNOWLEDGE BASE PAGE ====================
const KnowledgeBasePage = function() {
  var { user, token } = useAuth();
  var { theme, toggleTheme } = useTheme();
  var navigate = useNavigate();
  var isAdmin = user && user.is_admin;
  // Platform super-admin = admin with no organization.
  var isSuperAdmin = isAdmin && (!user || !user.organization_id);

  // Phase RAG-4.5: super-admin tenant impersonation. When a super-admin picks
  // a tenant, every /rag/* call carries `?as_tenant=<org_id>` and the rest of
  // the page renders as if the super-admin were that tenant's admin.
  var [activeTenant, setActiveTenantState] = useState(getActiveTenant());
  var [tenantList, setTenantList] = useState([]);
  // Effective "is super-admin without a chosen tenant" — drives the picker UI.
  var isSuperAdminWithoutOrg = isSuperAdmin && !activeTenant;

  // Load tenant directory once for the picker (super-admin only).
  useEffect(function() {
    if (!isSuperAdmin || !token) return;
    (async function() {
      try {
        var r = await fetch(API_URL + '/admin/tenants', { headers: getAuthHeaders() });
        if (r.ok) {
          var data = await readBody(r);
          setTenantList((data && data.tenants) || []);
        }
      } catch (e) { /* non-fatal */ }
    })();
  }, [isSuperAdmin, token]);

  function pickTenant(orgId) {
    setActiveTenant(orgId);
    setActiveTenantState(orgId);
    // Sync URL so deep-links share state.
    try {
      var sp = new URLSearchParams(window.location.search);
      if (orgId) sp.set('as_tenant', orgId); else sp.delete('as_tenant');
      var qs = sp.toString();
      var newUrl = window.location.pathname + (qs ? ('?' + qs) : '');
      window.history.replaceState(null, '', newUrl);
    } catch (e) {}
  }

  function exitImpersonation() {
    clearActiveTenant();
    setActiveTenantState(null);
  }

  // Read URL params for tab + prefill hints (deep-link from Knowledge Gap card)
  var urlParams = new URLSearchParams(window.location.search);
  var initialTab = urlParams.get('tab') || (isAdmin ? 'manage' : 'query');
  var prefillUpload = {
    vendor: urlParams.get('suggest_vendor') || '',
    domain: urlParams.get('suggest_domain') || '',
    docType: urlParams.get('suggest_doctype') || '',
    query: urlParams.get('suggest_query') || ''
  };
  var hasPrefill = prefillUpload.vendor || prefillUpload.domain || prefillUpload.docType || prefillUpload.query;

  var [tab, setTab] = useState(initialTab);
  var [documents, setDocuments] = useState([]);
  var [docsLoading, setDocsLoading] = useState(false);
  var [stats, setStats] = useState(null);

  // Enterprise document manager state
  var [docPage, setDocPage] = useState(0);
  var [totalDocs, setTotalDocs] = useState(0);
  var [totalDocsAll, setTotalDocsAll] = useState(0);
  var [docStatusCounts, setDocStatusCounts] = useState({ indexed: 0, queued: 0, error: 0 });
  var [docFilters, setDocFilters] = useState({ search: '', vendor: '', domain: '', status: 'all', sort_by: 'newest' });

  function mergeDocFilters(patch) {
    setDocFilters(function(prev) { return Object.assign({}, prev, patch); });
    // Any filter change resets to page 0
    if (!('page' in patch)) setDocPage(0);
  }

  var loadDocuments = useCallback(async function(page, filters) {
    setDocsLoading(true);
    try {
      var p = page != null ? page : 0;
      var f = filters || {};
      var params = new URLSearchParams({
        skip: String(p * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (f.search) params.set('search', f.search);
      if (f.vendor) params.set('vendor', f.vendor);
      if (f.domain) params.set('domain', f.domain);
      if (f.status && f.status !== 'all') params.set('status', f.status);
      if (f.sort_by && f.sort_by !== 'newest') params.set('sort_by', f.sort_by);
      var res = await fetch(withTenant(API_URL + '/rag/documents?' + params.toString()), { headers: getAuthHeaders() });
      if (res.ok) {
        var data = await readBody(res);
        setDocuments(data.documents || []);
        setTotalDocs(data.total || 0);
        setTotalDocsAll(data.total_all || data.total || 0);
        if (data.status_counts) setDocStatusCounts(data.status_counts);
      }
    } catch (e) {}
    setDocsLoading(false);
  }, []);

  var loadStats = useCallback(async function() {
    if (!isAdmin) return;
    try {
      var res = await fetch(withTenant(API_URL + '/rag/stats'), { headers: getAuthHeaders() });
      if (res.ok) setStats(await readBody(res));
    } catch (e) {}
  }, [isAdmin]);

  // Reload documents whenever page or filters change
  useEffect(function() {
    if (token && !isSuperAdminWithoutOrg) loadDocuments(docPage, docFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docPage, docFilters, token, isSuperAdminWithoutOrg]);

  useEffect(function() {
    // Skip RAG calls entirely for platform super-admins (no org_id) — they
    // hit 403s on every /rag/* endpoint by design and the empty-state card
    // explains what to do instead.
    if (token && !isSuperAdminWithoutOrg) {
      loadStats();
    }
  }, [token, loadStats, isSuperAdminWithoutOrg]);

  function refreshDocs() { loadDocuments(docPage, docFilters); }

  var handleDeleteDoc = async function(docId, filename, silent) {
    if (!silent && !window.confirm('Delete "' + filename + '" from knowledge base?')) return;
    try {
      var res = await fetch(withTenant(API_URL + '/rag/documents/' + docId), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Delete failed');
      if (!silent) { toast.success('Document deleted'); refreshDocs(); loadStats(); }
    } catch (e) { if (!silent) toast.error('Failed to delete document'); }
  };

  var handleBulkDeleteDocs = async function(docIds) {
    try {
      await Promise.all(docIds.map(function(docId) {
        return fetch(withTenant(API_URL + '/rag/documents/' + docId), {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
      }));
      toast.success('Deleted ' + docIds.length + ' document(s)');
      refreshDocs();
      loadStats();
    } catch (e) { toast.error('Bulk delete failed: ' + (e.message || '')); }
  };

  var handleBulkRetryDocs = async function(docs) {
    var successes = 0;
    for (var i = 0; i < docs.length; i++) {
      try {
        var res = await fetch(withTenant(API_URL + '/rag/ingest/document/' + docs[i].id + '/retry'), {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (res.ok) successes++;
      } catch (e) {}
    }
    toast.success('Re-queued ' + successes + ' of ' + docs.length + ' document(s)');
    refreshDocs();
  };

  var handleRetryDoc = async function(doc) {
    try {
      var res = await fetch(withTenant(API_URL + '/rag/ingest/document/' + doc.id + '/retry'), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      var body = await readBody(res);
      if (!res.ok) {
        toast.error((body && body.detail) || 'Retry failed');
        return;
      }
      toast.success('Re-queued indexing for ' + (doc.filename || doc.id));
      refreshDocs();
    } catch (e) {
      toast.error('Network error retrying indexing');
    }
  };

  var tabs = isAdmin
    ? [
        { id: 'manage', label: 'Manage Knowledge', icon: Database },
        { id: 'query', label: 'Query Knowledge Base', icon: BookOpen },
        { id: 'analytics', label: 'Analytics', icon: Activity }
      ]
    : [
        { id: 'query', label: 'Query Knowledge Base', icon: BookOpen }
      ];

  var tabButtons = [];
  for (var ti = 0; ti < tabs.length; ti++) {
    var t = tabs[ti];
    var TabIcon = t.icon;
    tabButtons.push(
      <button
        key={t.id}
        onClick={((tabId) => function() { setTab(tabId); })(t.id)}
        className={'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ' + (tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary')}
        data-testid={'kb-tab-' + t.id}
      >
        <TabIcon className="h-4 w-4" />
        {t.label}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border/50 bg-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/chat" className="flex items-center gap-2">
              <img src="/queryverse-logo.png" alt="QueryVerse" className="h-8" />
            </Link>
            <div className="h-6 w-px bg-border/50" />
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              <h1 className="font-semibold text-lg">Knowledge Base</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary transition-colors">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/chat">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="back-to-chat">
                <Sparkles className="h-4 w-4 mr-1.5" /> General AI
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {isSuperAdminWithoutOrg ? (
        <div className="max-w-3xl mx-auto w-full px-6 py-16">
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8 text-left"
            data-testid="kb-superadmin-blocked-card"
          >
            <div className="flex items-start gap-4">
              <div className="rounded-xl bg-amber-500/15 p-3">
                <Database className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1 space-y-3">
                <h2 className="text-xl font-semibold">Operate as a tenant</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  You are signed in as a <strong>platform super-admin</strong> ({user && user.email}).
                  Knowledge Base data is tenant-scoped. Pick a tenant below to manage their docs
                  and run RAG queries on their behalf — every action is recorded in the impersonation audit log.
                </p>
                <div className="rounded-lg border border-border/50 bg-background/40 p-4 space-y-3 text-sm">
                  <p className="font-medium" data-testid="kb-tenant-picker-label">Choose a tenant</p>
                  {tenantList.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Loading tenants…</p>
                  ) : (
                    <div className="space-y-1.5 max-h-64 overflow-auto" data-testid="kb-tenant-picker-list">
                      {tenantList.map(function(t) {
                        return (
                          <button
                            key={t.id}
                            onClick={function() { pickTenant(t.id); }}
                            data-testid={'kb-tenant-pick-' + t.id}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
                          >
                            <span className="flex flex-col">
                              <span className="font-medium">{t.name || t.id}</span>
                              <span className="text-xs text-muted-foreground">{t.id}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {t.doc_count || 0} docs · {t.query_count || 0} queries
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground pt-1 border-t border-border/30">
                    Tenants are also available at <Link to="/admin/tenants" className="text-primary underline-offset-2 hover:underline">Margin Monitor → Tenant Directory</Link>.
                  </p>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Link to="/admin">
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="kb-go-to-margin-monitor">
                      Open Margin Monitor
                    </Button>
                  </Link>
                  <Link to="/chat">
                    <Button size="sm" variant="ghost" className="rounded-full" data-testid="kb-back-to-chat">
                      Back to General AI
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
      {/* Impersonation banner — visible to super-admin while operating as a tenant */}
      {isSuperAdmin && activeTenant && (
        <div
          className="max-w-7xl mx-auto w-full px-6 pt-3"
          data-testid="kb-impersonation-banner"
        >
          <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-500" />
              <span>
                Operating as tenant <strong data-testid="kb-active-tenant-label">{activeTenant}</strong> — every action is audited.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={activeTenant}
                onChange={function(e) { pickTenant(e.target.value); }}
                className="rounded-md bg-background/50 border border-border/50 px-2 py-1 text-xs"
                data-testid="kb-tenant-switcher"
              >
                {tenantList.map(function(t) {
                  return <option key={t.id} value={t.id}>{t.name || t.id}</option>;
                })}
              </select>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-full text-xs h-7"
                onClick={exitImpersonation}
                data-testid="kb-exit-impersonation"
              >
                Exit
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div className="max-w-7xl mx-auto w-full px-6 pt-4">
        <div className="flex items-center gap-2 mb-4" data-testid="kb-tabs">
          {tabButtons}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col">
        {tab === 'manage' && isAdmin && (
          <div className="max-w-7xl mx-auto w-full px-6 pb-6 space-y-6" data-testid="manage-tab">
            {/* Stats row */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border/50 bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{stats.total_documents}</p>
                  <p className="text-xs text-muted-foreground">Documents</p>
                </div>
                <div className="rounded-xl border border-border/50 bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{stats.indexed_chunks}</p>
                  <p className="text-xs text-muted-foreground">Indexed Chunks</p>
                </div>
                <div className="rounded-xl border border-border/50 bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{stats.total_queries}</p>
                  <p className="text-xs text-muted-foreground">Total Queries</p>
                </div>
                <div className="rounded-xl border border-border/50 bg-card p-4 text-center">
                  <p className="text-2xl font-bold">{stats.today_queries}</p>
                  <p className="text-xs text-muted-foreground">Today's Queries</p>
                </div>
              </div>
            )}

            <BulkIngestPanel onIngestStarted={function() { loadDocuments(); loadStats(); }} />
            <UploadPanel onUploadComplete={function() { loadDocuments(); loadStats(); }} prefill={hasPrefill ? prefillUpload : null} />
            <DocumentList
              documents={documents}
              total={totalDocs}
              totalAll={totalDocsAll}
              statusCounts={docStatusCounts}
              page={docPage}
              pageSize={PAGE_SIZE}
              filters={docFilters}
              onFilterChange={mergeDocFilters}
              onPageChange={setDocPage}
              onDelete={handleDeleteDoc}
              onRetry={handleRetryDoc}
              onBulkDelete={handleBulkDeleteDocs}
              onBulkRetry={handleBulkRetryDocs}
              loading={docsLoading}
            />
          </div>
        )}

        {tab === 'query' && (
          <div className="flex-1 flex flex-col" data-testid="query-tab">
            <RAGChat user={user} />
          </div>
        )}

        {tab === 'analytics' && isAdmin && (
          <div className="max-w-7xl mx-auto w-full px-6 pb-6 space-y-6" data-testid="analytics-tab">
            {stats && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Vendor Distribution */}
                  <div className="rounded-xl border border-border/50 bg-card p-5">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" /> Vendor Distribution
                    </h3>
                    {stats.vendor_distribution && stats.vendor_distribution.length > 0 ? (
                      <div className="space-y-2">
                        {(function() {
                          var items = [];
                          for (var vi = 0; vi < stats.vendor_distribution.length; vi++) {
                            var vd = stats.vendor_distribution[vi];
                            items.push(
                              <div key={vi} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
                                <span className="text-sm">{vd.vendor || 'Untagged'}</span>
                                <span className="text-sm font-bold tabular-nums">{vd.count}</span>
                              </div>
                            );
                          }
                          return items;
                        })()}
                      </div>
                    ) : <p className="text-xs text-muted-foreground">No data</p>}
                  </div>

                  {/* Domain Distribution */}
                  <div className="rounded-xl border border-border/50 bg-card p-5">
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Network className="h-4 w-4 text-muted-foreground" /> Domain Distribution
                    </h3>
                    {stats.domain_distribution && stats.domain_distribution.length > 0 ? (
                      <div className="space-y-2">
                        {(function() {
                          var items = [];
                          for (var di = 0; di < stats.domain_distribution.length; di++) {
                            var dd = stats.domain_distribution[di];
                            items.push(
                              <div key={di} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30">
                                <span className="text-sm">{dd.domain || 'Untagged'}</span>
                                <span className="text-sm font-bold tabular-nums">{dd.count}</span>
                              </div>
                            );
                          }
                          return items;
                        })()}
                      </div>
                    ) : <p className="text-xs text-muted-foreground">No data</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
